import { useMsal } from '@azure/msal-react';
import {
  Avatar,
  Button,
  Collapse,
  Container,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Paper,
  TextField,
  Tooltip,
  Typography,
  Box
} from '@material-ui/core';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import {
  ArrowBackRounded,
  VisibilityOffRounded,
  VisibilityRounded
} from '@material-ui/icons';
import { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GetForgotPasswordParams } from '../api/reset-password-external-users';
import GradientText from '../components/GradientText';
import plugAndPlayConfig from '../config/plugAndPlay';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useAppDispatch } from '../hooks/useAppRudux';
import SCGlogo from './../assets/logoSCG_none.png';
import { redirectAfterLogin, signInRedirect } from './Auth/authSCG';
import loadingSlice from './Loading/loadingSlice';
import { getForgotPasswordAsync } from './ResetPassword/resetPasswordSlice';
import { getExternalUserAsync, getPlugAndPlayUserAsync } from './UserProfile/userProfileSlice';

interface AccountState {
  email: string;
  password: string;
  showPassword: boolean;
}

export default function Login() {
  const { instance } = useMsal();
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  const [account, setAccount] = useState<AccountState>({
    email: '',
    password: '',
    showPassword: false
  });
  const [openLoginEmail, setOpenLoginEmail] = useState<boolean>(false);
  const [openForgotPassword, setOpenForgotPassword] = useState<boolean>(false);

  const disabled = !(
    Boolean(account.email) &&
    (Boolean(account.password) || openForgotPassword)
  );

  function handleClickShowPassword() {
    setAccount({ ...account, showPassword: !account.showPassword });
  }

  function handleMouseDownPassword(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault();
  }

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      if (openForgotPassword) {
        const forgotPassword: GetForgotPasswordParams = {
          email: account.email
        };
        await dispatch(getForgotPasswordAsync(forgotPassword)).unwrap();
        dispatch(
          loadingSlice.actions.success({
            message: t('login.resetEmailSentMessage')
          })
        );
      } else {
        if (plugAndPlayConfig.isEnabled) {
          // Plug and Play mode: ใช้ internal authentication API
          await dispatch(getPlugAndPlayUserAsync(account)).unwrap();
        } else {
          // Legacy mode: ใช้ external authentication API
          await dispatch(getExternalUserAsync(account)).unwrap();
        }
        redirectAfterLogin(history);
        dispatch(loadingSlice.actions.clear());
      }
    } catch {
      setAccount({ ...account, password: '' });
    }
  }

  return (
    <Container component="main" maxWidth="md">
      <Box position="fixed" top={16} right={16} zIndex={1000}>
        <LanguageSwitcher />
      </Box>
      <Grid container>
        <Grid item xs={12} sm={6}>
          <div className={classes.title__box}>
            <Typography
              className={classes.appName}
              color="primary"
              variant="h5"
            >
              <GradientText>backspace</GradientText>
            </Typography>
            <Typography
              className={classes.title}
              color="textPrimary"
              variant="h5"
            >
              {t('login.welcome')}
            </Typography>
          </div>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Paper className={classes.paper}>
            <Collapse className={classes.form} in={!openLoginEmail}>
              <Typography
                className={classes.loginText}
                color="textPrimary"
                variant="h5"
              >
                {t('login.title')}
              </Typography>
              {!plugAndPlayConfig.isEnabled && (
                <>
                  <Typography color="textSecondary" variant="subtitle1">
                    {t('login.scgUserLabel')}
                  </Typography>
                  <Button
                    className={classes.buttonSCG}
                    variant="contained"
                    size="large"
                    fullWidth
                    startIcon={
                      <Avatar className={classes.small} src={SCGlogo}></Avatar>
                    }
                    onClick={() => signInRedirect(instance)}
                  >
                    {t('login.signInButton')}
                  </Button>
                  <div className={classes.dividerBox}>
                    <Divider />
                    <Typography variant="caption">{t('login.dividerOr')}</Typography>
                    <Divider />
                  </div>
                  <Typography color="textSecondary" variant="subtitle1">
                    {t('login.externalUserLabel')}
                  </Typography>
                </>
              )}
              <Button
                className={classes.buttonEmail}
                color="primary"
                variant="contained"
                size="large"
                fullWidth
                onClick={() => setOpenLoginEmail(true)}
              >
                {t('login.continueWithEmail')}
              </Button>
              <div className={classes.forgotButtonBox}>
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setOpenLoginEmail(true);
                    setOpenForgotPassword(true);
                  }}
                  disableRipple
                >
                  {t('login.forgotPassword')}
                </Button>
              </div>
            </Collapse>
            <Collapse in={openLoginEmail}>
              <div className={classes.titleAlignCenter}>
                <Tooltip title={t('login.backButtonTooltip') as string}>
                  <IconButton
                    aria-label={t('login.backButtonAriaLabel') as string}
                    edge="start"
                    onClick={() => {
                      setOpenLoginEmail(false);
                      setTimeout(() => {
                        setOpenForgotPassword(false);
                      }, 200);
                    }}
                  >
                    <ArrowBackRounded />
                  </IconButton>
                </Tooltip>
                <Typography color="textPrimary" variant="h6">
                  {openForgotPassword
                    ? t('login.resetPasswordTitle')
                    : t('login.loginExternalTitle')}
                </Typography>
              </div>
              {openForgotPassword && (
                <Typography className={classes.forgotText} variant="body2">
                  {t('login.resetPasswordDescription')}
                </Typography>
              )}
              <form className={classes.form} onSubmit={onSubmit}>
                <TextField
                  className={classes.input}
                  id="email"
                  label={t('login.emailLabel')}
                  placeholder={t('login.emailPlaceholder') as string}
                  type="email"
                  variant="outlined"
                  size="small"
                  value={account.email}
                  onChange={(event) =>
                    setAccount({ ...account, email: event.target.value })
                  }
                />
                {!openForgotPassword && (
                  <FormControl className={classes.input} variant="outlined">
                    <InputLabel htmlFor="password" margin="dense">
                      {t('login.passwordLabel')}
                    </InputLabel>
                    <OutlinedInput
                      id="password"
                      type={account.showPassword ? 'text' : 'password'}
                      style={{ height: 40 }}
                      value={account.password}
                      onChange={(event) =>
                        setAccount({ ...account, password: event.target.value })
                      }
                      endAdornment={
                        <InputAdornment position="end">
                          <IconButton
                            aria-label={t('login.togglePasswordAriaLabel') as string}
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                          >
                            {account.showPassword ? (
                              <VisibilityOffRounded />
                            ) : (
                              <VisibilityRounded />
                            )}
                          </IconButton>
                        </InputAdornment>
                      }
                      labelWidth={75}
                      placeholder={t('login.passwordPlaceholder') as string}
                    />
                  </FormControl>
                )}
                <Button
                  className={classes.button}
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="large"
                  disabled={disabled}
                  fullWidth
                >
                  {openForgotPassword ? t('login.sendResetLinkButton') : t('login.loginButton')}
                </Button>
                <div className={classes.forgotButtonBox}>
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setOpenForgotPassword(!openForgotPassword)}
                    disableRipple
                  >
                    {openForgotPassword
                      ? t('login.backToLogin')
                      : t('login.forgotPassword')}
                  </Button>
                </div>
              </form>
            </Collapse>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    paper: {
      maxWidth: 400,
      backgroundColor:
        theme.palette.type === 'light'
          ? theme.palette.background.paper
          : '#21262c',
      borderRadius: '16px',
      marginTop: theme.spacing(16),
      marginBottom: theme.spacing(4),
      padding: theme.spacing(1, 2),
      [theme.breakpoints.down('sm')]: {
        maxWidth: '100%'
      },
      [theme.breakpoints.down('xs')]: {
        marginTop: theme.spacing(4)
      }
    },
    title__box: {
      margin: theme.spacing(24, 4, 0, 0),
      [theme.breakpoints.down('xs')]: {
        textAlign: 'center',
        margin: theme.spacing(6, 0, 0, 0)
      }
    },
    title: {
      marginTop: theme.spacing(1),
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.2rem'
      },
      [theme.breakpoints.down('xs')]: {
        fontSize: '1rem'
      }
    },
    loginText: {
      textAlign: 'center',
      margin: theme.spacing(2, 0, 2.5, 0)
    },
    titleAlignCenter: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(1, 0)
    },
    form: {
      display: 'block',
      width: '100%' // Fix IE 11 issue.
    },
    button: {
      marginTop: theme.spacing(3),
      marginBottom: theme.spacing(1),
      padding: '0.5rem 2rem',
      fontSize: 16
    },
    buttonEmail: {
      marginBottom: theme.spacing(1),
      padding: '0.5rem 2rem',
      fontSize: 16
    },
    buttonSCG: {
      padding: '0.5rem 1rem',
      fontSize: 16,
      width: '100%',
      color: '#ffffff',
      backgroundColor: '#ED1B24',
      '&:hover, &.Mui-focusVisible': {
        backgroundColor: '#D41721'
      }
    },
    dividerBox: {
      display: 'flex',
      alignItems: 'center',
      margin: theme.spacing(1.2, 0, 0.25, 0),
      '& > *': {
        '&:first-child': {
          flexGrow: 1,
          marginRight: theme.spacing(1)
        },
        '&:last-child': {
          flexGrow: 1,
          marginLeft: theme.spacing(1)
        }
      }
    },
    forgotText: {
      fontSize: 12,
      marginBottom: theme.spacing(0.5),
      color: theme.palette.text.secondary
    },
    forgotButtonBox: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      '& > *': {
        textTransform: 'none',
        textDecoration: 'underline',
        marginBottom: theme.spacing(1),
        '&:hover, &.Mui-focusVisible': {
          color: theme.palette.primary.main,
          backgroundColor: 'transparent'
        }
      }
    },
    small: {
      width: theme.spacing(3.5),
      height: theme.spacing(3.5)
    },
    input: {
      margin: '0.5rem 0',
      width: '100%'
    },
    appName: {
      fontFamily: 'Comfortaa',
      fontSize: '3.5rem',
      fontWeight: 700,
      letterSpacing: -2,
      [theme.breakpoints.up('sm')]: {
        marginLeft: theme.spacing(-0.25)
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: '3rem'
      }
    }
  })
);
